import {isUserAssignedToSchedule} from '@/shared/lib/schedule-moderators';

/** Missing/null means off — moderators only see sessions they are assigned to. */
export const DEFAULT_ALLOW_MODERATORS_VIEW_ALL_SESSIONS = false;

type ProjectAccessFlag = {
  allow_moderators_view_all_sessions?: boolean | null;
} | null | undefined;

type AssignedSessionViewGroup<T extends {rows: Array<{schedule?: unknown}>}> = T;

/** Missing/null means off — a moderator is scoped to their own assignments. */
export const allowsModeratorsViewAllSessions = (
  project: ProjectAccessFlag,
): boolean => {
  if (!project || project.allow_moderators_view_all_sessions == null) {
    return DEFAULT_ALLOW_MODERATORS_VIEW_ALL_SESSIONS;
  }
  return project.allow_moderators_view_all_sessions !== false;
};

export const isModeratorSessionAccessRestricted = (
  isModerator: boolean,
  project: ProjectAccessFlag,
): boolean => isModerator && !allowsModeratorsViewAllSessions(project);

const MODERATOR_TASK_NAV_PATHS = new Set(['/tasks', '/moderator/tasks']);

/**
 * Sidebar entries that open the project task list. Restricted moderators reach
 * tasks through session prep and completed sessions instead.
 */
export const withoutModeratorTaskNav = <T extends {path: string}>(
  items: readonly T[],
  restricted: boolean,
): T[] =>
  restricted
    ? items.filter((item) => !MODERATOR_TASK_NAV_PATHS.has(item.path))
    : [...items];

/**
 * Whether a moderator's task entries should be hidden.
 *
 * A project in view answers it on its own. The role menus are project-agnostic
 * though, and an unknown project must not read as permission: with none in view
 * the entries only go away when every project the moderator belongs to
 * restricts them, so an unrestricted project keeps its tasks reachable.
 */
export const isModeratorTaskNavRestricted = (
  isModerator: boolean,
  activeProject: ProjectAccessFlag,
  projects: readonly ProjectAccessFlag[] = [],
): boolean => {
  if (!isModerator) return false;
  if (activeProject) return !allowsModeratorsViewAllSessions(activeProject);
  return (
    projects.length > 0 &&
    projects.every((project) => !allowsModeratorsViewAllSessions(project))
  );
};

/** Drop session-view rows the viewer is not assigned to (primary or secondary). */
export const restrictSessionViewGroupsToAssigned = <
  T extends {rows: Array<{schedule?: unknown}>},
>(
  groups: AssignedSessionViewGroup<T>[],
  userId: string | null | undefined,
): T[] => {
  if (!userId) return [];
  return groups
    .map((group) => ({
      ...group,
      rows: group.rows.filter((row) =>
        isUserAssignedToSchedule(row.schedule, userId),
      ),
    }))
    .filter((group) => group.rows.length > 0);
};
